import { cn } from '@/shared/utilities';
import { Typography } from '@mui/material';
import { memo } from 'react';
import type { LinkAttachment } from '../../models/attachment';

interface ReplyLinkPreviewProps {
  attachment: LinkAttachment;
}

export const ReplyLinkPreview = memo<ReplyLinkPreviewProps>(({ attachment }) => {
  // Extract domain from URL for display
  const getDomainFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace('www.', '');
    } catch {
      return 'Unknown site';
    }
  };

  const domain = attachment.url ? getDomainFromUrl(attachment.url) : 'Unknown site';
  const displayTitle = attachment.filename || domain;

  return (
    <div className='mex:flex mex:items-center mex:gap-2'>
      <div className='mex:h-8 mex:w-8 mex:flex-shrink-0 mex:overflow-hidden mex:rounded mex:bg-gray-200'>
        {attachment.imageUrl ? (
          <img
            src={attachment.imageUrl}
            alt={displayTitle}
            className='mex:h-full mex:w-full mex:object-cover'
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextElementSibling?.classList.remove('hidden');
            }}
          />
        ) : null}
        {/* Placeholder icon */}
        <div
          className={cn(
            'mex:flex mex:h-full mex:w-full mex:items-center mex:justify-center mex:bg-gray-200',
            attachment.imageUrl ? 'mex:hidden' : 'mex:flex'
          )}
        >
          <svg className='mex:h-4 mex:w-4 mex:text-gray-400' fill='currentColor' viewBox='0 0 20 20' aria-hidden='true'>
            <path
              fillRule='evenodd'
              d='M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z'
              clipRule='evenodd'
            />
          </svg>
        </div>
      </div>
      <div className='mex:min-w-0 mex:flex-1'>
        <Typography variant='caption' className='mex:block mex:text-gray-600 mex:text-xs'>
          {domain}
        </Typography>
        <Typography variant='body2' noWrap sx={{ color: 'text.primary' }} title={displayTitle}>
          {displayTitle}
        </Typography>
      </div>
    </div>
  );
});
